import { createFileRoute } from "@tanstack/react-router";
import { FaqSection, faqSchema, RelatedGuides, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

const URL = "https://palstars.com/afk-gold";

const TITLE = "Palworld AFK Gold Farm: 30k+ Gold Per Hour Setup";
const DESCRIPTION =
  "The overnight Palworld AFK gold farm: base layout, worker suitability levels, merchant sell loop and gold per hour for each setup. Updated for Palworld 1.0.";

const FAQ: FaqItem[] = [
  {
    q: "What is the best AFK gold farm in Palworld?",
    a: "A dedicated cake or ore base feeding a merchant sell loop is the best AFK gold farm in Palworld. Three Gathering-3 workers on a Berry Plantation plus two Handiwork-2 crafters at a Cooking Pot produce cakes overnight, and a full chest of cakes sells for roughly 20,000-30,000 gold per hour of real farm uptime.",
  },
  {
    q: "How much gold per hour can you AFK farm in Palworld?",
    a: "A single well-staffed base returns about 15,000-20,000 gold per hour, and a second base dedicated to ore and ingots pushes the combined total past 30,000 gold per hour. Gold only lands when you sell, so the number is realised at the merchant, not while you idle.",
  },
  {
    q: "Which Pals should work the AFK gold base?",
    a: "Use Pals whose suitability meets the station requirement exactly: Gathering 2-3 (Lifmunk, Cinnamoth), Handiwork 2-3 (Lifmunk, Vixy, Anubis for level 3+), Kindling 2 (Foxparks, Arsox) and Transporting 2 (Lunaris, Cattiva). A worker one level below the station requirement simply never starts the job.",
  },
  {
    q: "Do Palworld bases keep producing while you are offline?",
    a: "In single player the world pauses when you quit, so production stops. On a dedicated server the base keeps running while you are away, which is what makes the overnight AFK gold loop worth building on a server.",
  },
  {
    q: "What is the fastest way to make money in Palworld early game?",
    a: "Sell Pal Spheres you never use, then move to Berries and Baked Berries as soon as you have two Gathering workers. From roughly level 25 onwards, cakes and refined ingots outperform every early item because merchants buy them in stacks at a stable price.",
  },
  {
    q: "Does the AFK gold farm need a specific base layout?",
    a: "Yes. Keep the plantation, Cooking Pot, feed box and chests inside a six-tile radius so haulers never path outside the base, place the feed box adjacent to the workstations, and leave one open tile per station so two Pals never contest the same job node.",
  },
  {
    q: "How do you stop Pals from getting stuck in an AFK base?",
    a: "Flatten the ground before placing stations, avoid stacked foundations near workbenches, cap the base at 15 workers, and keep exactly one bed and one hot spring per three Pals so sanity recovery does not queue every worker onto one object.",
  },
  {
    q: "Is AFK gold farming worth it versus selling raid drops?",
    a: "AFK farming wins on consistency, raids win on burst. A cake base pays every real hour without input, while raid drops and rare schematics pay far more per run but need active fights. Most players fund breeding costs from the AFK base and buy legendary schematics with raid gold.",
  },
];

export const Route = createFileRoute("/afk-gold")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld AFK Gold Farm — Base Layout & Gold Per Hour" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld AFK Gold Farm — Base Layout & Gold Per Hour" },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["AFK Gold Farm", "/afk-gold"]]),
      webPageSchema({
        name: "Palworld AFK Gold Farm",
        description: DESCRIPTION,
        path: "/afk-gold",
      }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: AfkGoldPage,
});

const SETUPS = [
  {
    name: "Cake line (best gold per hour)",
    gold: "20,000-30,000 gold/hr",
    unlock: "Level 25 — Cooking Pot + Berry Plantation",
    workers: "3x Gathering 2-3, 2x Handiwork 2, 1x Kindling 2, 1x Transporting 2",
    notes: "Cakes double as breeding fuel, so the same base funds gold and eggs at once.",
  },
  {
    name: "Ore to ingot loop",
    gold: "12,000-18,000 gold/hr",
    unlock: "Level 20 — Primitive Furnace + ore node base",
    workers: "4x Mining 2, 2x Kindling 2, 2x Transporting 2",
    notes: "Refine before selling: ingots sell for far more than raw ore per inventory slot.",
  },
  {
    name: "Berry starter loop",
    gold: "4,000-7,000 gold/hr",
    unlock: "Level 8 — Berry Plantation + Feed Box",
    workers: "2x Gathering 1-2, 1x Kindling 1",
    notes: "The first loop worth building; upgrade it into the cake line rather than rebuilding.",
  },
  {
    name: "Ranch drop farm",
    gold: "6,000-9,000 gold/hr",
    unlock: "Level 12 — Ranch",
    workers: "Chikipi, Mozzarina, Beegarde, Woolipop on Farming suitability",
    notes: "Zero-maintenance income; wool and milk sell steadily and never spoil in chests.",
  },
];

function AfkGoldPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld money guide</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld AFK Gold Farm</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          The best Palworld AFK gold farm is a cake base feeding a merchant sell loop: three Gathering workers on a
          Berry Plantation, two Handiwork crafters on a Cooking Pot and one Kindling Pal produce roughly
          20,000-30,000 gold per hour of farm uptime. Ore-to-ingot and ranch loops add another 12,000-18,000 gold per
          hour on a second base.
        </p>
      </header>

      <section className="mt-8 grid gap-4 sm:grid-cols-2">
        {SETUPS.map((s) => (
          <article key={s.name} className="glass rounded-2xl p-5">
            <h2 className="text-lg font-bold">{s.name}</h2>
            <p className="mt-1 text-sm font-semibold text-primary">{s.gold}</p>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex gap-2"><dt className="w-24 text-muted-foreground">Unlock</dt><dd className="font-medium">{s.unlock}</dd></div>
              <div className="flex gap-2"><dt className="w-24 text-muted-foreground">Workers</dt><dd className="font-medium">{s.workers}</dd></div>
            </dl>
            <p className="aeo-answer mt-3 text-sm text-muted-foreground">{s.notes}</p>
          </article>
        ))}
      </section>

      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Overnight AFK base checklist</h2>
        <ol className="aeo-answer mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          <li>Flatten the build area first — uneven ground is the single biggest cause of stuck workers.</li>
          <li>Keep every station, the feed box and the chests inside a six-tile radius of each other.</li>
          <li>Cap the base at 15 workers and one bed plus one hot spring per three Pals for sanity recovery.</li>
          <li>Match suitability exactly: a Handiwork 1 Pal never starts a Handiwork 2 station.</li>
          <li>Stock the feed box with cakes or salads so nobody stops working to forage.</li>
          <li>Run it on a dedicated server if you want production to continue while you are offline.</li>
          <li>Sell in bulk at the Wandering Merchant — gold is only realised at the sell step.</li>
        </ol>
      </section>

      <InternalLinkBlock path="/afk-gold" />
      <FaqSection items={FAQ} heading="Palworld AFK gold farming FAQ" />
      <RelatedGuides
        links={[
          { slug: "farming", label: "Palworld Farming Guide" },
          { slug: "palworld-breeding-guide", label: "Palworld Breeding Guide" },
          { slug: "palworld-gold-mining", label: "Palworld Gold Mining Guide" },
        ]}
      />
    </div>
  );
}
